"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const steps = [
  {
    number: "01",
    icon: "UserPlusIcon",
    title: "Create Pet Profile",
    description:
      "Sign up free and add your pet's details: name, breed, age, weight, medical history, allergies, and vaccination records. Takes under 3 minutes.",
    color: "#5EBFB3",
    bg: "#E8F7F6",
  },
  {
    number: "02",
    icon: "LinkIcon",
    title: "Connect Professionals",
    description:
      "Search for vets, groomers, trainers, and other pet services near you. Send connection requests and choose what information each professional can see.",
    color: "#7C3AED",
    bg: "#EDE9FE",
  },
  {
    number: "03",
    icon: "BellAlertIcon",
    title: "Get Smart Reminders",
    description:
      "Receive automated reminders for vaccine due dates, vet appointments, grooming sessions, and medication schedules. Never miss important care.",
    color: "#0369A1",
    bg: "#E0F2FE",
  },
  {
    number: "04",
    icon: "MapPinIcon",
    title: "Track & Share Safely",
    description:
      "Log check-ins to keep track of your pet's whereabouts. Share emergency contact info and medical details instantly if your pet ever gets lost.",
    color: "#059669",
    bg: "#DCFCE7",
  },
];

export default function PetOwnerFlow() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-16 scroll-fade-up">
          <span className="teal-badge mb-4">For Pet Owners</span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-[#1A2B2B] mt-4 mb-5 tracking-tight">
            Your pet's life, organized in one place
          </h2>
          <p className="text-lg text-[#4A6363] max-w-2xl mx-auto leading-relaxed">
            From profile creation to emergency sharing, here's how DogFob keeps your pet safe and your life simple.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* Connector line (desktop) */}
          <div
            className="hidden lg:block absolute top-[52px] left-[calc(12.5%+24px)] right-[calc(12.5%+24px)] h-[2px] z-0"
            style={{
              background:
                "linear-gradient(to right, #5EBFB3, #7C3AED, #0369A1, #059669)",
            }}
          />

          {steps.map((step, idx) => (
            <div
              key={step.number}
              className={`scroll-fade-up stagger-${idx + 1} relative z-10`}
            >
              <div className="feature-card p-6 h-full flex flex-col">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 relative animate-pulse-glow"
                  style={{ background: step.bg }}
                >
                  <Icon
                    name={step.icon as "UserPlusIcon"}
                    size={24}
                    style={{ color: step.color }}
                  />
                  <span
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full text-white text-[10px] font-extrabold font-heading flex items-center justify-center"
                    style={{ background: step.color }}
                  >
                    {idx + 1}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-[#1A2B2B] mb-3 font-heading leading-snug">
                  {step.title}
                </h3>
                <p className="text-[#4A6363] leading-relaxed text-sm flex-1">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}